// onboarding-completo.js
//
// La regla de "Quién eres" obligatorio (#1599): qué preguntas hay, en qué pantalla va
// cada una, cuáles se ven según lo que ya respondió, cuándo una pantalla está completa y
// qué body manda el PATCH. Puro, sin React: lo usan el wizard (`VerificationWizard`), la
// pantalla de lo que falta (`CompletarDatos`) y la ruta que decide si se muestra
// (`RequiereOnboardingCompleto`).
//
// PAR EN EL BACKEND: `backend/app/user_api/onboarding_completo.py` repite el orden, las
// opciones y la regla del nombre. Son dos repos: si cambia uno, cambia el otro. Cubre R14,
// R16, R17, R18, R22, R26, R35, R46 y R47 — .claude/specs/1599-onboarding-calificar/
//
// Sebastián, 10-oct-2026: "faltan preguntas completas en el cuentanos quien eres, nombre
// completo si o si" y "nos interesa mas info para calificar". Por eso son obligatorias,
// salvo las que después pidió como opcionales ("deja estos ultimos como opcionales no
// obligatorios"): herramientas y frustraciones. «Cómo nos conociste» dejó de serlo en #1738 R18.
//
// #1738 (R1, R3, R4, R6): la etapa tiene 6 opciones y abre una pregunta hija: `especialidad`
// (becado y especialista) o `profesion_no_medico` (no soy médico). PAR EN EL BACKEND: los
// campos nuevos del `PATCH /api/user/onboarding/` son `especialidad` (hasta 120) y
// `profesion_no_medico` (KINESIOLOGIST, NURSE, TENS, OTHER, NO_SALUD) y, desde la v2, `universidad`
// (hasta 200) en `OnboardingAnswersSerializer` (backend/app/user_api/serializers.py). Las hijas NO
// entran a `CAMPOS_ONBOARDING` ni a `onboarding_faltante` (R5): solo cuentan cuando la persona
// está respondiendo la etapa. A las cuentas que ya usan la app se les pide por otra lista,
// `reingreso_faltante` de /me (R20, ver `preguntasDeFaltante`).
// Desde la v2 el canal («Cómo nos conociste») es obligatorio en el alta (R18), pero NO entra a
// `onboarding_faltante`: las cuentas que ya pasaron el alta no lo vuelven a ver.
// .claude/specs/1738-etapa-alta/

import { hijasDeEtapa, universidadOpcionalEn } from "./etapas";
import { CANALES_OPCIONES } from "./canales";

export const TIPOS_TURNO = ["uci", "uti", "sala", "urgencia_hosp", "urgencia"];
export const DIAS = ["lun", "mar", "mie", "jue", "vie", "sab", "dom"];
export const HERRAMIENTAS = [
  "claude",
  "chatgpt",
  "google_drive",
  "bloc_notas",
  "word",
  "papel",
  "notas_celular",
  "ninguna",
  "otra",
];
export const FRUSTRACIONES = [
  "evoluciones",
  "ingresos",
  "epicrisis",
  "entrega_turno",
  "examenes",
  "indicaciones",
  "otro",
];
export const CANALES = CANALES_OPCIONES.map((o) => o.value);
// #1850: con quién trabaja en el turno (opcional). «nadie» es excluyente, como «ninguna».
export const EQUIPO = ["internos", "becados", "backup", "nadie"];

// padre -> [opción que abre el texto, campo del texto]. El texto solo se ve con esa opción
// marcada y es siempre opcional (R46).
export const OTRO_DE = {
  herramientas_turno: ["otra", "herramientas_turno_otra"],
  frustraciones: ["otro", "frustraciones_otro"],
  como_nos_conocio: ["otro", "como_nos_conocio_otro"],
};

// v4: nunca cuentan para Continuar, para el 400 del backend ni para `onboarding_faltante`.
// #1738 R18: `como_nos_conocio` ya NO es opcional en el alta; su texto de «otro» sigue siéndolo.
export const OPCIONALES = [
  "equipo_turno",
  "herramientas_turno",
  "herramientas_turno_otra",
  "frustraciones",
  "frustraciones_otro",
  "como_nos_conocio_otro",
];

// El orden ES la regla (R7 del backend): `onboarding_faltante` llega así.
export const CAMPOS_ONBOARDING = [
  "full_name",
  "etapa",
  "donde_trabaja",
  "ambito",
  "carga_documentacion",
  "turnos_semana",
  "tipos_turno",
  "proximo_turno",
  "que_hace_sin_turnos",
  "trabaja_hospitalizados",
  "dias_hospitalizados",
];

export const esOpcional = (campo) => OPCIONALES.includes(campo);

// #1738 R14: la universidad es opcional solo para médico general y especialista; para estudiante,
// interno y becado es obligatoria. Por eso depende de la etapa elegida.
export const esOpcionalEn = (campo, v) =>
  esOpcional(campo) || (campo === "universidad" && universidadOpcionalEn(v?.etapa));

// Las pantallas de Quién eres. La 5ª es "Tu turno" o "Qué haces" según los turnos. #1850: de 4
// pasaron a 6 («Tu trabajo» sale de la 1ª y «Dónde vive tu turno» de la 2ª) para que cada una quepa
// en una vista del celular sin scroll; las preguntas y el guardado son los mismos.
export const VISTAS = {
  TU: "quien_eres",
  TU_TRABAJO: "tu_trabajo",
  SEMANA: "semana",
  LUGARES: "donde_vive_tu_turno",
  TU_TURNO: "tu_turno",
  QUE_HACES: "que_haces",
  COMO: "como_nos_conociste",
};

export const valoresVacios = () => ({
  full_name: "",
  etapa: "",
  especialidad: "",
  universidad: "",
  profesion_no_medico: "",
  donde_trabaja: "",
  ambito: [],
  carga_documentacion: "",
  turnos_semana: null,
  tipos_turno: [],
  proximo_turno: "",
  que_hace_sin_turnos: "",
  trabaja_hospitalizados: null,
  dias_hospitalizados: [],
  herramientas_turno: [],
  herramientas_turno_otra: "",
  equipo_turno: [],
  frustraciones: [],
  frustraciones_otro: "",
  como_nos_conocio: "",
  como_nos_conocio_otro: "",
});

// Lo que devuelve `GET /api/user/onboarding/` (o nada) sobre el molde de arriba.
export const valoresDeServidor = (data) => {
  const base = valoresVacios();
  if (!data || typeof data !== "object") return base;
  const lista = (v) => (Array.isArray(v) ? v : []);
  const texto = (v) => (typeof v === "string" ? v : "");
  return {
    full_name: texto(data.full_name),
    etapa: texto(data.etapa),
    especialidad: texto(data.especialidad),
    universidad: texto(data.universidad),
    profesion_no_medico: texto(data.profesion_no_medico),
    donde_trabaja: texto(data.donde_trabaja),
    ambito: lista(data.ambito),
    carga_documentacion: texto(data.carga_documentacion),
    turnos_semana: typeof data.turnos_semana === "string" && data.turnos_semana ? data.turnos_semana : null,
    tipos_turno: lista(data.tipos_turno),
    proximo_turno: texto(data.proximo_turno),
    que_hace_sin_turnos: texto(data.que_hace_sin_turnos),
    trabaja_hospitalizados: typeof data.trabaja_hospitalizados === "boolean" ? data.trabaja_hospitalizados : null,
    dias_hospitalizados: lista(data.dias_hospitalizados),
    herramientas_turno: lista(data.herramientas_turno),
    herramientas_turno_otra: texto(data.herramientas_turno_otra),
    equipo_turno: lista(data.equipo_turno),
    frustraciones: lista(data.frustraciones),
    frustraciones_otro: texto(data.frustraciones_otro),
    como_nos_conocio: texto(data.como_nos_conocio),
    como_nos_conocio_otro: texto(data.como_nos_conocio_otro),
  };
};

// Lo ya escrito en pantalla gana sobre lo que llega del servidor (la precarga es asíncrona:
// quien alcanzó a tocar algo no pierde lo suyo).
export const mezclarPrecarga = (servidor, local) => {
  const out = { ...servidor };
  Object.keys(local).forEach((k) => {
    const v = local[k];
    const vacio = v === null || v === "" || (Array.isArray(v) && v.length === 0);
    if (!vacio) out[k] = v;
  });
  return out;
};

export const normalizarTexto = (t) => String(t == null ? "" : t).trim().replace(/\s+/g, " ");

// Misma regla que `nombre_completo_valido` del backend: al menos 2 palabras de 2 o más
// letras; solo letras (con tildes y ñ), espacios, apóstrofo, guion y punto; hasta 120.
const SOLO_LETRAS_Y_SIGNOS = /^[\p{L}\s'’.-]+$/u;
export const nombreCompletoValido = (texto) => {
  const nombre = normalizarTexto(texto);
  if (!nombre || nombre.length > 120) return false;
  if (!SOLO_LETRAS_Y_SIGNOS.test(nombre)) return false;
  const palabras = nombre
    .split(" ")
    .filter((p) => (p.match(/\p{L}/gu) || []).length >= 2);
  return palabras.length >= 2;
};

// Hoy en America/Santiago como YYYY-MM-DD: es el `min` del control de fecha y lo que el
// backend toma como "hoy" (R3). Con la fecha del navegador en UTC, a las 23:30 de Chile el
// médico no podría elegir el turno de hoy.
export const hoyEnSantiago = (ahora = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(ahora);

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;
// El backend acepta de hoy a hoy + 365 días (`onboarding_completo.rango_proximo_turno`).
// Sin el tope acá, una fecha a más de un año pasaba el front y el back la rechazaba con
// un mensaje que no se entendía (revisor pre-deploy #1599, 11-oct).
export const maxProximoTurno = (hoy) => {
  const d = new Date(`${hoy}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 365);
  return d.toISOString().slice(0, 10);
};
const fechaValida = (f, hoy) => FECHA_RE.test(f || "") && f >= hoy && f <= maxProximoTurno(hoy);

export const hace = (v) => ["1", "2-3", "4+"].includes(v.turnos_semana);
export const sinTurnos = (v) => v.turnos_semana === "0";

// Las pantallas del recorrido, en orden (#1850: 6).
export const pantallasQuienEres = (v) => [
  VISTAS.TU,
  VISTAS.TU_TRABAJO,
  VISTAS.SEMANA,
  VISTAS.LUGARES,
  sinTurnos(v) ? VISTAS.QUE_HACES : VISTAS.TU_TURNO,
  VISTAS.COMO,
];
export const TOTAL_PANTALLAS_QUIEN_ERES = 6;

// `view` de la pantalla `i` (0 a 5) del recorrido.
export const vistaDePantalla = (i, v) =>
  pantallasQuienEres(v)[Math.max(0, Math.min(TOTAL_PANTALLAS_QUIEN_ERES - 1, i))];

// Preguntas VISIBLES de una pantalla, en el orden en que se pintan. Incluye las opcionales y
// las condicionales que su respuesta abre (los textos de otro solo con su opción marcada).
export const camposDePantalla = (vista, v) => {
  if (vista === VISTAS.TU) {
    // #1738 R3/R4: la hija va justo después de la etapa y solo con la etapa que la abre.
    return ["full_name", "etapa", ...hijasDeEtapa(v.etapa)];
  }
  if (vista === VISTAS.TU_TRABAJO) return ["donde_trabaja", "ambito", "carga_documentacion"];
  if (vista === VISTAS.SEMANA) {
    const out = ["turnos_semana", "trabaja_hospitalizados"];
    if (v.trabaja_hospitalizados === true) out.push("dias_hospitalizados");
    // #1850: con quién trabaja (internos, becados, backup). Opcional, para no sumar fricción.
    out.push("equipo_turno");
    return out;
  }
  if (vista === VISTAS.LUGARES) {
    // Orden del mockup B: qué te frustra y después herramientas.
    const out = ["frustraciones"];
    if (v.frustraciones.includes("otro")) out.push("frustraciones_otro");
    out.push("herramientas_turno");
    if (v.herramientas_turno.includes("otra")) out.push("herramientas_turno_otra");
    return out;
  }
  if (vista === VISTAS.TU_TURNO) return ["tipos_turno", "proximo_turno"];
  if (vista === VISTAS.QUE_HACES) return ["que_hace_sin_turnos"];
  const out = ["como_nos_conocio"];
  if (v.como_nos_conocio === "otro") out.push("como_nos_conocio_otro");
  return out;
};

// ¿Esta pregunta tiene respuesta? (`hoy` = YYYY-MM-DD para validar la fecha.)
export const campoRespondido = (campo, v, hoy = hoyEnSantiago()) => {
  switch (campo) {
    case "full_name":
      return nombreCompletoValido(v.full_name);
    case "etapa":
    case "especialidad":
    case "universidad":
    case "profesion_no_medico":
    case "donde_trabaja":
    case "carga_documentacion":
    case "como_nos_conocio":
    case "herramientas_turno_otra":
    case "frustraciones_otro":
    case "como_nos_conocio_otro":
      return normalizarTexto(v[campo]).length > 0;
    case "ambito":
    case "tipos_turno":
    case "dias_hospitalizados":
    case "herramientas_turno":
    case "frustraciones":
    case "equipo_turno":
      return Array.isArray(v[campo]) && v[campo].length > 0;
    case "turnos_semana":
      return v.turnos_semana !== null && v.turnos_semana !== undefined && v.turnos_semana !== "";
    case "trabaja_hospitalizados":
      return v.trabaja_hospitalizados === true || v.trabaja_hospitalizados === false;
    case "proximo_turno":
      return fechaValida(v.proximo_turno, hoy);
    case "que_hace_sin_turnos": {
      const t = normalizarTexto(v.que_hace_sin_turnos);
      return t.length > 0 && t.length <= 200;
    }
    default:
      return false;
  }
};

// Obligatorias visibles de una lista de campos (las opcionales y sus textos no cuentan).
// Con `v`, la universidad de médico general y especialista (opcional según la etapa) tampoco cuenta.
export const obligatoriasDe = (campos, v = null) => campos.filter((c) => !esOpcionalEn(c, v));

// ¿Se puede apretar Continuar en esta pantalla? Solo cuentan las obligatorias (v4).
export const pantallaCompleta = (vista, v, hoy = hoyEnSantiago()) =>
  obligatoriasDe(camposDePantalla(vista, v), v).every((c) => campoRespondido(c, v, hoy));

// "N de M respondidas" de la cabecera: solo obligatorias.
export const conteoDePantalla = (vista, v, hoy = hoyEnSantiago()) => {
  const obligatorias = obligatoriasDe(camposDePantalla(vista, v), v);
  return {
    total: obligatorias.length,
    hechas: obligatorias.filter((c) => campoRespondido(c, v, hoy)).length,
  };
};

// Todas las preguntas visibles del recorrido completo (para armar el PATCH único).
export const camposDelRecorrido = (v) =>
  pantallasQuienEres(v).flatMap((vista) => camposDePantalla(vista, v));

// Ninguna es excluyente: al marcarla se desmarcan las demás herramientas y al marcar otra
// se desmarca Ninguna (R47).
export const alternarHerramienta = (lista, valor) => {
  const actual = Array.isArray(lista) ? lista : [];
  if (valor === "ninguna") return actual.includes("ninguna") ? [] : ["ninguna"];
  const sinNinguna = actual.filter((x) => x !== "ninguna");
  return sinNinguna.includes(valor) ? sinNinguna.filter((x) => x !== valor) : [...sinNinguna, valor];
};

// #1850: «nadie» es excluyente en «con quién trabajas» (mismo patrón que «ninguna»).
export const alternarEquipo = (lista, valor) => {
  const actual = Array.isArray(lista) ? lista : [];
  if (valor === "nadie") return actual.includes("nadie") ? [] : ["nadie"];
  const sinNadie = actual.filter((x) => x !== "nadie");
  return sinNadie.includes(valor) ? sinNadie.filter((x) => x !== valor) : [...sinNadie, valor];
};

export const alternarEnLista = (lista, valor) => {
  const actual = Array.isArray(lista) ? lista : [];
  return actual.includes(valor) ? actual.filter((x) => x !== valor) : [...actual, valor];
};

// Al responder una pregunta que cierra condicionales, lo que ya no aplica se limpia para que
// no viaje ni se muestre al volver (R16: volver y cambiar a "0" quita tipos y fecha).
export const aplicarCambio = (v, campo, valor) => {
  const sig = { ...v, [campo]: valor };
  if (campo === "turnos_semana") {
    if (valor === "0") {
      sig.tipos_turno = [];
      sig.proximo_turno = "";
    } else {
      sig.que_hace_sin_turnos = "";
    }
  }
  if (campo === "trabaja_hospitalizados" && valor !== true) sig.dias_hospitalizados = [];
  // #1738 R6: al cambiar a una etapa que cierra la hija, se limpia para que no viaje en el PATCH
  // ni reaparezca al volver a abrirla.
  if (campo === "etapa") {
    const hijas = hijasDeEtapa(valor);
    if (!hijas.includes("especialidad")) sig.especialidad = "";
    if (!hijas.includes("universidad")) sig.universidad = "";
    if (!hijas.includes("profesion_no_medico")) sig.profesion_no_medico = "";
  }
  Object.entries(OTRO_DE).forEach(([padre, [opcion, texto]]) => {
    const marcado = Array.isArray(sig[padre]) ? sig[padre].includes(opcion) : sig[padre] === opcion;
    if (!marcado) sig[texto] = "";
  });
  return sig;
};

// Body del PATCH (R18): las obligatorias y condicionales visibles de las `vistas` pedidas y,
// de las opcionales, SOLO las que tienen respuesta (lista con algo, universidad escrita, texto de
// otro no vacío con su opción marcada). Nunca manda una opcional vacía: pisaría lo guardado.
export const cuerpoPatch = (v, campos, hoy = hoyEnSantiago()) => {
  const body = {};
  campos.forEach((campo) => {
    if (esOpcionalEn(campo, v)) {
      if (!campoRespondido(campo, v, hoy)) return;
      body[campo] = typeof v[campo] === "string" ? normalizarTexto(v[campo]) : v[campo];
      return;
    }
    if (!campoRespondido(campo, v, hoy)) return;
    if (campo === "full_name" || campo === "que_hace_sin_turnos" || campo === "especialidad" || campo === "universidad") {
      body[campo] = normalizarTexto(v[campo]);
    } else if (campo === "donde_trabaja") {
      body[campo] = normalizarTexto(v[campo]);
    } else {
      body[campo] = v[campo];
    }
  });
  return body;
};

// R26: del `onboarding_faltante` de /me solo cuentan las claves que la pantalla de lo que
// falta sabe preguntar. Una clave desconocida (un backend más nuevo) o de una pregunta
// opcional (un backend de la v3) no atrapa a nadie.
export const faltantesConocidos = (lista) =>
  Array.isArray(lista) ? CAMPOS_ONBOARDING.filter((c) => lista.includes(c)) : [];

// R20 (#1738): las claves de `reingreso_faltante` de /me que la pantalla sabe preguntar. Una clave
// desconocida (un backend más nuevo) no atrapa a nadie, igual que `faltantesConocidos`.
export const REINGRESO_CAMPOS = ["etapa", "especialidad", "universidad"];
export const reingresoConocido = (lista) =>
  Array.isArray(lista) ? REINGRESO_CAMPOS.filter((c) => lista.includes(c)) : [];

// Preguntas que pinta la pantalla de lo que falta: las de la lista y, anidadas dentro de la
// pregunta que las abre, las condicionales que la respuesta actual abra (R23). Si el padre
// NO está en la lista (ya respondido antes), la condicional faltante va como pregunta propia.
// #1738 R22: `reingreso` (la lista `reingreso_faltante` de /me) suma, para una cuenta que ya usa
// la app, la etapa nueva con sus hijas (si tenía «Staff» u «Otro») o la especialidad y la
// universidad sueltas (si la etapa que tiene ya vale). `onboarding_faltante` manda: lo que ya
// pregunta no se repite.
export const preguntasDeFaltante = (faltantes, v, reingreso = []) => {
  const lista = faltantesConocidos(faltantes);
  const hijos = (padre) => {
    if (padre === "turnos_semana") {
      if (hace(v)) return ["tipos_turno", "proximo_turno"];
      if (sinTurnos(v)) return ["que_hace_sin_turnos"];
      return [];
    }
    // #1738: si lo que falta es la etapa, sus hijas se preguntan junto (el backend las exige al responder la etapa, R5, R15).
    if (padre === "etapa") return hijasDeEtapa(v.etapa);
    if (padre === "trabaja_hospitalizados") return v.trabaja_hospitalizados === true ? ["dias_hospitalizados"] : [];
    return [];
  };
  const principales = lista.filter(
    (c) =>
      !(
        ["tipos_turno", "proximo_turno", "que_hace_sin_turnos"].includes(c) && lista.includes("turnos_semana")
      ) && !(c === "dias_hospitalizados" && lista.includes("trabaja_hospitalizados")),
  );
  const out = principales.map((campo) => ({ campo, hijos: hijos(campo) }));
  const reing = reingresoConocido(reingreso);
  if (reing.includes("etapa") && !lista.includes("etapa")) {
    out.push({ campo: "etapa", hijos: hijasDeEtapa(v.etapa) });
  } else if (!lista.includes("etapa")) {
    // La etapa ya vale: solo se piden las hijas que le faltan, sueltas.
    const abiertas = hijasDeEtapa(v.etapa);
    reing.filter((c) => c !== "etapa" && abiertas.includes(c)).forEach((campo) => out.push({ campo, hijos: [] }));
  }
  return out;
};

// Todos los campos visibles de la pantalla de lo que falta (para completitud y body).
export const camposDeFaltante = (faltantes, v, reingreso = []) =>
  preguntasDeFaltante(faltantes, v, reingreso).flatMap(({ campo, hijos }) => [campo, ...hijos]);

// Una etapa legada (staff, otro) no es una respuesta: en el reingreso hay que elegir una de las 6.
const ETAPAS_VALIDAS = ["estudiante", "interno", "medico_general", "becado", "especialista", "no_medico"];

export const faltanteCompleto = (faltantes, v, hoy = hoyEnSantiago(), reingreso = []) => {
  const campos = camposDeFaltante(faltantes, v, reingreso);
  const obligatorias = obligatoriasDe(campos, v);
  const etapaOk = !campos.includes("etapa") || ETAPAS_VALIDAS.includes(v.etapa);
  return campos.length > 0 && etapaOk && obligatorias.every((c) => campoRespondido(c, v, hoy));
};

// Texto de "falta ..." para la bajada de la pantalla de lo que falta (mockup F).
const FRASE_FALTANTE = {
  full_name: "tu nombre completo",
  etapa: "en qué etapa estás",
  donde_trabaja: "dónde trabajas",
  ambito: "dónde ejerces",
  carga_documentacion: "cuánto te toma documentar",
  turnos_semana: "tus turnos",
  tipos_turno: "qué tipo de turnos haces",
  proximo_turno: "cuándo es tu próximo turno",
  que_hace_sin_turnos: "qué haces (dijiste que no haces turnos)",
  trabaja_hospitalizados: "si trabajas con hospitalizados",
  dias_hospitalizados: "qué días trabajas con hospitalizados",
  especialidad: "tu especialidad",
  universidad: "dónde estudias",
};
export const fraseDeFaltante = (faltantes, reingreso = []) => {
  const claves = [...faltantesConocidos(faltantes), ...reingresoConocido(reingreso).filter((c) => !faltantesConocidos(faltantes).includes(c))];
  const frases = claves.map((c) => FRASE_FALTANTE[c]);
  if (frases.length <= 1) return frases[0] || "";
  return `${frases.slice(0, -1).join(", ")} y ${frases[frases.length - 1]}`;
};
